import {
  AspectRatio,
  Box,
  Button,
  Center,
  Stack,
  useMediaQuery,
} from '@chakra-ui/react';
import { IMedia } from 'interfaces/media.interface';
import { IProduct, IProductVariation } from 'interfaces/product.interface';
import Image from 'next/image';
import { IProductGallary } from 'pages/products/[slug]';
import { useEffect, useState } from 'react';
import { FreeMode, Pagination, Thumbs } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import ProductGallerySwiperLightBox from './ProductGallerySwiperLightBox';
import ProductGallerySwiperVideoLightBox from './ProductGallerySwiperVideoLightBox';

type ProductGallerySwiperProps = {
  product: IProduct;
  currentProductVariation?: IProductVariation;
  images: IProductGallary[];
};

function ProductGallerySwiper({
  product,
  currentProductVariation,
  images,
}: ProductGallerySwiperProps) {
  // use media query
  const [isMobile] = useMediaQuery('(max-width: 767px)');

  // use media query
  const [isDesktop] = useMediaQuery('(min-width: 1200px)');

  // main swiper
  const [controlledSwiper, setControlledSwiper] = useState<any>();

  // thumbnail swiper
  const [thumbsSwiper, setThumbsSwiper] = useState<any>();

  const [thumbsSwiperMobile, setThumbsSwiperMobile] = useState<any>();

  // main swiper end reached
  const [_, setIsReachEnded] = useState<boolean>(false);

  // image view lightbox
  const [imageSelected, setImageSelected] = useState<IMedia>();

  // video lightbox visible
  const [videoLightBoxVisible, setVideoLightBoxVisible] =
    useState<boolean>(false);

  // current active index
  const [activeIndex, setActiveIndex] = useState<number>(0);

  // ** set active index when currentProductVariation change
  useEffect(() => {
    // ** Note: we need to slieTo index of currentProductVariation image in main swiper
    if (currentProductVariation && images && images.length > 0) {
      const index = images.findIndex(
        (image) => image.uniqueId === currentProductVariation.id
      );

      // log currentProductVariation
      console.log('currentProductVariation', currentProductVariation.id);

      // log index
      console.log('index', index);

      if (index !== -1) {
        setActiveIndex(index);
        controlledSwiper?.slideTo(index, 0);
      }
    } else {
      setActiveIndex(0);
      controlledSwiper?.slideTo(0, 0);
    }
  }, [currentProductVariation, images, controlledSwiper]);

  return (
    <>
      <Box position="relative" height="100%">
        <Box height="100%" position="absolute" left="-96px" width="66px">
          {images.length > 0 && (
            <Swiper
              modules={[Thumbs, FreeMode]}
              spaceBetween={10}
              slidesPerView={5}
              direction="vertical"
              freeMode={true}
              watchSlidesProgress={true}
              onSwiper={(swiper) => {
                setThumbsSwiper(swiper);
              }}
              style={{ height: controlledSwiper?.size }}
              threshold={15}
              grabCursor
            >
              {images.map((image, index) => (
                <SwiperSlide key={index}>
                  {({ isActive }) => (
                    <AspectRatio ratio={1}>
                      <Center height="100%" w="100%" cursor="pointer">
                        <Image
                          src={image.url ?? '/image-icon/empty-image.png'}
                          alt={image.name}
                          width={100}
                          height={100}
                          quality={95}
                          priority={true}
                          style={{
                            objectFit: 'cover',
                            objectPosition: 'center',
                            height: '100%',
                          }}
                        ></Image>
                      </Center>
                    </AspectRatio>
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </Box>

        <Box>
          {images.length > 0 ? (
            <Swiper
              modules={[Thumbs, Pagination]}
              spaceBetween={2}
              slidesPerView={1}
              onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
              onSwiper={(swiper) => setControlledSwiper(swiper)}
              onReachEnd={() => setIsReachEnded(true)}
              onReachBeginning={() => setIsReachEnded(false)}
              grabCursor
              pagination={{ clickable: true }}
              thumbs={{
                swiper:
                  isDesktop && thumbsSwiper
                    ? thumbsSwiper
                    : isMobile && thumbsSwiperMobile
                    ? thumbsSwiperMobile
                    : null,
              }}
              threshold={15}
              loopFillGroupWithBlank={false}
            >
              {images.map((image, index) => (
                <SwiperSlide
                  key={index}
                  style={{
                    width: '100%',
                    height: '100%',
                    maxHeight: controlledSwiper?.size,
                  }}
                >
                  <Image
                    src={image.url ?? '/image-icon/empty-image.png'}
                    alt={image.name}
                    width={500}
                    height={500}
                    quality={100}
                    priority={true}
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                      height: '100%',
                    }}
                  ></Image>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <Center height="100%" width="100%">
              <Image
                src="/image-icon/empty-image.png"
                alt="empty-image"
                width={500}
                height={500}
                quality={100}
              ></Image>
            </Center>
          )}
        </Box>

        <Stack position="absolute" bottom={3} left={3} zIndex="docked">
          {product.videoUrl && (
            <Button
              onClick={() => setVideoLightBoxVisible(true)}
              colorScheme="gray"
              bg="gray.100"
              minW="auto"
              w="44px"
              height="44px"
              borderRadius="100%"
              padding="0"
              _hover={{
                bg: 'gray.50',
              }}
              pl={1}
            >
              <Image
                src="/image-icon/play.png"
                alt="play"
                width={18}
                height={24}
              ></Image>
            </Button>
          )}
          {images.length > 0 && (
            <Button
              onClick={() => setImageSelected(images[activeIndex])}
              colorScheme="gray"
              bg="gray.100"
              minW="auto"
              w="44px"
              height="44px"
              borderRadius="100%"
              padding="0"
              _hover={{
                bg: 'gray.50',
              }}
            >
              <Image
                src="/image-icon/arrow-zoom.png"
                alt="zoom"
                width={24}
                height={24}
              ></Image>
            </Button>
          )}
        </Stack>

        {controlledSwiper ? (
          <>
            <Button
              position="absolute"
              left={4}
              top="50%"
              transform="translateY(-50%)"
              colorScheme="gray"
              bg="gray.100"
              minW="auto"
              w="30px"
              height="30px"
              borderRadius="100%"
              padding="0"
              _hover={{
                bg: 'gray.50',
              }}
              _active={{
                bg: 'gray.50',
              }}
              zIndex="docked"
              onClick={() => controlledSwiper.slidePrev()}
              isDisabled={controlledSwiper.isBeginning}
              aria-label="Previous"
              background="gray.100"
              display={{
                base: 'none',
                lg: 'inline-flex',
              }}
            >
              <Image
                src="/image-icon/arrow-prev.png"
                alt="prev"
                width={16}
                height={16}
              ></Image>
            </Button>
            <Button
              position="absolute"
              right={4}
              top="50%"
              transform="translateY(-50%)"
              colorScheme="gray"
              bg="gray.100"
              minW="auto"
              w="30px"
              height="30px"
              borderRadius="100%"
              padding="0"
              _hover={{
                bg: 'gray.50',
              }}
              _active={{
                bg: 'gray.50',
              }}
              zIndex="docked"
              onClick={() => controlledSwiper.slideNext()}
              isDisabled={controlledSwiper.isEnd}
              aria-label="Next"
              display={{
                base: 'none',
                lg: 'inline-flex',
              }}
            >
              <Image
                src="/image-icon/arrow-next.png"
                alt="prev"
                width={16}
                height={16}
              ></Image>
            </Button>
          </>
        ) : null}
      </Box>

      <Box
        bg="#EADECE"
        py={2.5}
        display={{
          lg: 'none',
        }}
      >
        {images.length > 0 ? (
          <Swiper
            modules={[Thumbs, FreeMode]}
            spaceBetween={10}
            slidesPerView={isMobile ? 4.5 : 5}
            direction="horizontal"
            freeMode={true}
            watchSlidesProgress={true}
            onSwiper={(swiper) => {
              setThumbsSwiperMobile(swiper);
            }}
            grabCursor
          >
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                {({ isActive }) => (
                  <AspectRatio ratio={1}>
                    <Center height="100%" w="100%" cursor="pointer">
                      <Image
                        src={image.url}
                        alt={image.name}
                        quality={90}
                        fill
                        style={{
                          objectFit: 'cover',
                        }}
                      ></Image>
                    </Center>
                  </AspectRatio>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        ) : null}
      </Box>

      <ProductGallerySwiperVideoLightBox
        videoUrl={product.videoUrl || ''}
        visible={videoLightBoxVisible}
        onClosed={() => setVideoLightBoxVisible(false)}
      ></ProductGallerySwiperVideoLightBox>

      <ProductGallerySwiperLightBox
        imageSelected={imageSelected}
        images={images}
        onClosed={() => setImageSelected(undefined)}
      ></ProductGallerySwiperLightBox>
    </>
  );
}

export default ProductGallerySwiper;
